import { computed, inject, Injectable } from '@angular/core';
import { IrisDmsSettingsAlias, IrisDmsSettingsPropertyI, IrisDmsSettingsPropertyMetaI } from '../models/IrisDmsSettings';
import { forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { cloneDeep, Dictionary, keyBy } from 'lodash';
import { BackendWorkflowApiService } from '@iris/common/services/workflow/backend-workflow-api.service';
import { IrisFlagService } from '@iris/common/services/flags.service';
import { TranslateService } from '@ngx-translate/core';
import { IrisTagType } from '@iris/common/models/IrisTag';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisQueryParamsOrderBy } from '@iris/api-query';
import { IrisDmsPropertiesService } from '@iris/modules/system-configuration/system-configuration-list/documents/services/dms-properties.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisDmsSettingsStore } from '../store/dms-settings.signal-store';
import { IrisSecurityService } from '../../security/services/security.service';
import { defaultContentPagination } from '../models/default-content-pagination';
import { ColDef } from 'ag-grid-community';

export const defaultDmsProperties: IrisDmsSettingsPropertyI[] = [
  { name: IrisTagType.FlagSymbols, isActive: false, isMultiple: false },
];

@Injectable({ providedIn: 'root' })
export class IrisDmsSettingsSandbox {
  private readonly workflowService = inject(BackendWorkflowApiService);
  private readonly flagService = inject(IrisFlagService);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(IrisUserService);
  private readonly dmsPropertiesService = inject(IrisDmsPropertiesService);
  private readonly securityService = inject(IrisSecurityService);

  private readonly dmsSettingsStore = inject(IrisDmsSettingsStore);

  readonly globalSettings$ = this.dmsPropertiesService.getProperties().pipe(
    shareReplay({
      bufferSize: 1,
      refCount: true,
    }),
  );
  readonly globalSettings = toSignal(this.globalSettings$);
  readonly foldersConfig = computed(() => cloneDeep(this.dmsSettingsStore.folderSettings()));
  readonly trashBinConfig = computed(() => cloneDeep(this.dmsSettingsStore.trashBinSettings()));
  readonly filterConfig = computed(() => cloneDeep(this.dmsSettingsStore.filterSettings()));
  readonly recentFilesConfig = computed(() => cloneDeep(this.dmsSettingsStore.recentFilesSettings()));
  readonly systemFolderConfig = computed(() => cloneDeep(this.dmsSettingsStore.systemFolderSettings()));
  readonly systemTrashBinConfig = computed(() => cloneDeep(this.dmsSettingsStore.systemTrashBinSettings()));

  readonly maxFileSizeInMB = computed(() => {
    const settings = this.globalSettings();
    return this.isAdminOrDmsConfigUser
      ? settings?.maxFileSizeAdmin || this.defaultMaxFileSizeMBAdmin
      : settings?.maxFileSizeNonAdmin || this.defaultMaxFileSizeMBNonAdmin;
  });

  readonly maxFileSizeInBytes = computed(() => this.maxFileSizeInMB() * this.bytesInMB);
  readonly dustbinDays = computed(() => this.globalSettings()?.dustbinDays ?? 0);

  readonly userGroupsEnabled = computed(() => this.globalSettings()?.userGroupsEnabled);

  properties$: Observable<IrisDmsSettingsPropertyI[]> = this.globalSettings$.pipe(
    switchMap((settings) => {
      const properties = (settings.properties || this.defaultProperties).filter(({ name }) => name !== IrisTagType.ProjectWorkflow as string);
      const activeProperties = properties.filter(property => property.isActive);
      const observables: Observable<IrisDmsSettingsPropertyI>[] = activeProperties.map((property: IrisDmsSettingsPropertyI) => {
        const meta = this.propertiesMeta[property.name];
        if (!meta) {
          return of({ ...property, directory: [] });
        }

        return meta.directory$(settings).pipe(
          map((data: unknown[]) => {
            return {
              ...property,
              directory: data,
              title: this.translateService.instant('label.' + property.name),
              model:  meta.model,
              backField: meta.backField,
              searchField: meta.searchField,
              showIcons: meta.showIcons,
              isForFiles: meta.isForFiles,
              isForFolders: meta.isForFolders,
            };
          }),
        );
      });

      return forkJoin(observables);
    }),
  );

  folderProperties$: Observable<IrisDmsSettingsPropertyI[]> = this.properties$.pipe(
    map(properties => properties.filter(({ isForFolders }) => isForFolders)),
  );

  readonly foldersOrderBy = computed(() => {
    const foldersConfig = this.foldersConfig();
    return foldersConfig?.settings?.orderBy?.length ? foldersConfig.settings.orderBy : defaultContentPagination.orderBy;
  });

  isPropertyActive$(propType: IrisTagType): Observable<boolean> {
    return this.properties$.pipe(
      map((props) => {
        const selectedProp = props.find((prop) => prop.name === propType as string);
        return !!selectedProp && selectedProp.isActive;
      }),
    );
  }

  private readonly defaultMaxFileSizeMBAdmin = 2000;
  private readonly defaultMaxFileSizeMBNonAdmin = 200;
  private readonly bytesInMB = 1048576;

  get isAdmin(): boolean {
    return this.securityService.isAdmin;
  }

  get isDmsConfigUser(): boolean {
    return this.securityService.hasConfigRights(IrisModules.DMS);
  }

  get isAdminOrDmsConfigUser(): boolean {
    return this.isAdmin || this.isDmsConfigUser;
  }

  private get propertiesMeta(): Dictionary<IrisDmsSettingsPropertyMetaI> {
    return keyBy<IrisDmsSettingsPropertyMetaI>([
      {
        name: 'ProjectWorkflow',
        directory$: (settings: { workflowId: number }) => settings.workflowId
          ? this.workflowService.getWorkflowStates(settings.workflowId)
          : of([]),
        model: 'workflowStateId',
        isForFiles: true,
        isForFolders: true,
      },
      {
        name: 'FlagSymbols',
        directory$: () => of(this.flagService.flags
          .map(({ id, name }) => ({ id, name: this.translateService.instant(name) }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        ),
        model: 'customIconClass',
        showIcons: '{"iconClass": "id", "optionLabel": "name"}',
        isForFiles: false,
        isForFolders: true,
      },
    ], 'name');
  }

  get dmsModuleName(): IrisModules {
    return IrisModules.DMS2;
  }

  get userId(): number {
    return this.userService.me.id;
  }

  get defaultProperties(): IrisDmsSettingsPropertyI[] {
    return cloneDeep(defaultDmsProperties);
  }

  getFolderSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.Folder);
  }

  getTrashBinSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.Trash);
  }

  getFilterSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.Filter);
  }

  getRecentFilesSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.RecentFiles);
  }

  getSystemFolderSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.SystemFolder);
  }

  getSystemTrashBinSettings(): void {
    this.getSettings(IrisDmsSettingsAlias.SystemTrash);
  }

  private getSettings(alias: IrisDmsSettingsAlias): void {
    this.dmsSettingsStore.fetchDmsSettings(alias);
  }

  updateUserColumnDefs(alias: IrisDmsSettingsAlias, columnDefs: ColDef[]): void {
    this.dmsSettingsStore.updateUserColumnDefs({ alias, columnDefs });
  }

  updateUserOrderBy(alias: IrisDmsSettingsAlias, orderBy: IrisQueryParamsOrderBy[]): void {
    this.dmsSettingsStore.updateUserOrderBy({ alias, orderBy });
  }
}
